import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';

import { adminApi } from '@/api';
import { Alert, Badge, Button, Card, CardHeader, ProgressBar, Skeleton } from '@/components/ui';
import { errorMessage } from '@/hooks/useApiError';
import { formatDate } from '@/utils/format';

import { studentSubscriptionPath } from './links';
import { formatMoney, formatPrice, usageRatio } from './money';
import { StudentPlanActions } from './StudentPlanActions';

/**
 * A student's plan on their admin record: what they are on, what they have
 * used this month, any invoice waiting, and the actions to change it.
 *
 * Keyed on the student rather than a subscription, because a student on Free —
 * most of them — has no subscription row, and they are exactly who gets
 * assigned a plan.
 */
export function StudentPlanPanel({ studentId, studentName }) {
  const billing = useQuery({
    queryKey: ['admin-student-billing', studentId],
    queryFn: () => adminApi.getStudentBilling(studentId),
  });

  if (billing.isPending) {
    return (
      <Card>
        <CardHeader title="Plan" />
        <div className="space-y-2" role="status" aria-label="Loading the plan">
          <Skeleton className="h-5 w-32" />
          <Skeleton className="h-4" />
          <Skeleton className="h-4" />
        </div>
      </Card>
    );
  }
  if (billing.isError) {
    return (
      <Card>
        <CardHeader title="Plan" />
        <Alert tone="error" title="Could not load the plan">
          <p>{errorMessage(billing.error)}</p>
          <Button
            variant="secondary"
            size="xs"
            className="mt-2"
            onClick={() => void billing.refetch()}
          >
            Try again
          </Button>
        </Alert>
      </Card>
    );
  }

  const data = billing.data;
  const pending = data.pending_invoice;

  return (
    <Card>
      <CardHeader
        title="Plan"
        action={
          data.subscription_id ? (
            <Link
              to={studentSubscriptionPath(data.subscription_id)}
              className="text-brand-700 text-sm hover:underline"
            >
              Subscription
            </Link>
          ) : null
        }
      />
      <p className="text-ink flex flex-wrap items-center gap-2 text-sm font-medium">
        {data.plan.name}
        {data.is_paid ? (
          <Badge dot tone={data.cancel_at_period_end ? 'warning' : 'success'}>
            {data.cancel_at_period_end ? 'Ending' : 'Paid'}
          </Badge>
        ) : (
          <Badge tone="neutral">Free</Badge>
        )}
        {data.application_boost && <Badge tone="brand">Premium badge</Badge>}
      </p>
      <p className="text-ink-muted mt-1 text-xs">
        {data.is_paid
          ? `${formatPrice(data.plan.price_cents, data.plan.currency, data.plan.interval)} · ${
              data.cancel_at_period_end ? 'ends' : 'renews'
            } ${formatDate(data.current_period_end)}`
          : 'No paid plan running.'}
      </p>

      {data.is_paid && data.scheduled_plan && (
        <Alert tone="info" className="mt-3">
          Moves to {data.scheduled_plan.name} on {formatDate(data.current_period_end)}.
        </Alert>
      )}

      {pending && (
        <Alert tone={data.starts_on_payment ? 'warning' : 'info'} className="mt-3">
          {data.starts_on_payment
            ? `Invoice ${pending.number} (${formatMoney(pending.amount_cents, pending.currency)}) is waiting. The plan starts when it is marked paid.`
            : `Active — invoice ${pending.number} (${formatMoney(pending.amount_cents, pending.currency)}) awaiting payment.`}
        </Alert>
      )}

      <ul className="mt-4 space-y-3">
        {data.usage.map((row) => {
          const ratio = usageRatio(row.used, row.limit);
          return (
            <li key={row.key}>
              <div className="mb-1 flex items-baseline justify-between gap-3 text-xs">
                <span className="text-ink font-medium">{row.label}</span>
                <span className="text-ink-muted tabular-nums">
                  {row.limit === null ? `${row.used} · unlimited` : `${row.used} of ${row.limit}`}
                </span>
              </div>
              {ratio !== null && <ProgressBar value={ratio} />}
            </li>
          );
        })}
      </ul>
      <p className="text-ink-muted mt-2 text-xs">This month ({data.usage_period}, UTC).</p>

      <div className="mt-4 flex flex-wrap gap-2">
        <StudentPlanActions studentId={studentId} studentName={studentName} billing={data} />
      </div>
    </Card>
  );
}
